/* eslint-disable no-return-assign */
/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, MutableRefObject, PropsWithChildren, Ref, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { VirtuosoHandle } from "react-virtuoso";
import { FlatNode, getFlatTree } from "../helpers/virtual-scrolling-helpers";
import { TreeBrowserNode } from "../types/data-types";
import { TreeBrowserProps } from "./tree-browser";
import { VirtualScrollingComponent } from "./virtual-scrolling-component";
import { VirtualScrollingWithHeaderComponent } from "./virtual-scrolling-with-header-component";

export interface VirtualScrollingForwardRef {
    scrollToBottom: () => void;
    heightChanged: () => void;
}

interface VirtualScrollingProps<TNode extends TreeBrowserNode, TExtraProps extends any = undefined> extends TreeBrowserProps<TNode, TExtraProps> {
    containerRef?: MutableRefObject<HTMLDivElement>;
    createFooterComponent?: (height: number, bottomNode?: FlatNode<TNode>) => JSX.Element;
    calculateRemainingVirtualHeight?: (flatNodes: FlatNode<TNode>[], openNodes: string[], containerHeight: number) => number;
    headerNodeIds?: string[];
}

export const VirtualScrollingTreeBrowserComponent = forwardRef(VirtualScrollingTreeBrowser);

/**
 * Component for browsing given tree of nodes.
 */
function VirtualScrollingTreeBrowser<TNode extends TreeBrowserNode, TExtraProps extends any = undefined>(
    {
        node,
        openNodes,
        components,
        displayRootNode,
        extraProps,
        parentNodeType,
        getInteractivity,
        containerRef,
        createFooterComponent,
        calculateRemainingVirtualHeight,
        headerNodeIds,
    }: PropsWithChildren<VirtualScrollingProps<TNode, TExtraProps>>,
    ref: Ref<VirtualScrollingForwardRef>
) {
    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const containerHeightRef = useRef<number>(0);

    const followOutput = useRef<undefined | "smooth">();

    const nodeFlatTree = useMemo(() => getFlatTree(node, displayRootNode, parentNodeType, openNodes), [node, openNodes]);

    const createFooter = useCallback(() => {
        const footerHeight = calculateRemainingVirtualHeight?.(nodeFlatTree, openNodes, containerHeightRef.current);
        return createFooterComponent != null ? createFooterComponent(footerHeight, nodeFlatTree[nodeFlatTree.length - 1]) : null;
    }, [nodeFlatTree, openNodes, containerHeightRef.current]);

    /** When height of the virtual scrolling content changes */
    const handleHeightChange = () => {
        containerHeightRef.current = Math.round(containerRef?.current?.getBoundingClientRect().height || 0);
    };
    useImperativeHandle(
        ref,
        () => ({
            scrollToBottom() {
                // Set to smooth when scrolling to ensure it always goes to bottom
                followOutput.current = "smooth";
                virtuosoRef.current?.scrollIntoView({
                    index: nodeFlatTree.length,
                    behavior: "smooth",
                    done: () => {
                        // Once scrolling has finished we no longer want to go to bottom when a new item is added
                        followOutput.current = undefined;
                    },
                });
            },
            heightChanged() {
                handleHeightChange();
            },
        }),
        []
    );

    return (
        <>
            {headerNodeIds == null ? (
                <VirtualScrollingComponent
                    handleHeightChange={handleHeightChange}
                    virtuosoRef={virtuosoRef}
                    nodeFlatTree={nodeFlatTree}
                    createFooter={createFooter}
                    components={components}
                    extraProps={extraProps}
                    getInteractivity={getInteractivity}
                    followOutput={followOutput.current}
                />
            ) : (
                <VirtualScrollingWithHeaderComponent
                    handleHeightChange={handleHeightChange}
                    virtuosoRef={virtuosoRef}
                    nodeFlatTree={nodeFlatTree}
                    createFooter={createFooter}
                    components={components}
                    extraProps={extraProps}
                    getInteractivity={getInteractivity}
                    followOutput={followOutput.current}
                    headerNodeIds={headerNodeIds}
                />
            )}
        </>
    );
}
